import { RfqDraft, RfqFormParams } from '@/features/rfq/types'
import { OrderSide } from '@/types/generated/common/trading'

/** A fresh RFQ ticket: one empty BUY leg, manual mode, anonymous, all-makers. */
export function defaultRfqDraft(): RfqDraft {
  return {
    legs: [{ symbol: '', amount: '', side: OrderSide.ORDER_SIDE_BUY }],
    hedge: {
      enabled: false,
      symbol: '',
      amount: '',
      side: OrderSide.ORDER_SIDE_BUY,
      price: '',
    },
    makerIds: [],
    disclosed: false,
    executionMode: 'manual',
    autoApprove: { kind: 'markOffsetPercent', value: '' },
    autoRetry: { enabled: false, maxRetries: '', retryUntilMs: '' },
  }
}

/** The non-leg RFQ params at their defaults (manual, anonymous, all-makers, no hedge). */
export function defaultRfqFormParams(): RfqFormParams {
  const { legs: _legs, ...params } = defaultRfqDraft()
  return params
}

/** Minimal shape of an order-form leg the RFQ ticket can be seeded from. */
export interface OrderFormLegSeed {
  symbol: string
  side: 'buy' | 'sell'
  amount?: string
}

/**
 * Seed a new ticket from the current order-form selection, so switching to the RFQ
 * mode carries the chosen instrument(s), side(s) and quantity over — mirroring how
 * TWAP / Limit Chase reuse the form's legs. Blank-symbol rows are dropped; an empty
 * selection falls back to a fresh default ticket (one empty BUY leg).
 */
export function orderItemsToRfqDraft(items: OrderFormLegSeed[]): RfqDraft {
  const base = defaultRfqDraft()
  const legs = items
    .filter((item) => item.symbol.trim() !== '')
    .map((item) => ({
      symbol: item.symbol,
      // Order rows store amounts comma-formatted ("1,250.5"); the RFQ proto + Decimal
      // validation need a plain decimal string.
      amount: (item.amount ?? '').replace(/,/g, ''),
      side: item.side === 'sell' ? OrderSide.ORDER_SIDE_SELL : OrderSide.ORDER_SIDE_BUY,
    }))
  return { ...base, legs: legs.length ? legs : base.legs }
}

/**
 * Combine the order-form legs with the RFQ-only params into a full ticket. Used by the
 * order-form RFQ mode, where the legs live in the shared form items and the params live
 * in `rfqFormParamsAtomFamily`.
 */
export function composeRfqDraft(items: OrderFormLegSeed[], params: RfqFormParams): RfqDraft {
  return { ...orderItemsToRfqDraft(items), ...params }
}
